import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    setFormState('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9pmab5i2vhsdh2d3770', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试');
      setFormState('error');
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20 bg-background-50">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold font-serif text-foreground-900 mb-4">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm leading-relaxed mb-8">
              如果您有任何关于91n使用的问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队会在1-2个工作日内回复您的消息。
            </p>

            <div className="flex flex-col gap-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-mail-line text-lg" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮件</h4>
                  <p className="text-sm text-foreground-500">support@91n.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 flex-shrink-0">
                  <i className="ri-customer-service-2-line text-lg" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">在线客服</h4>
                  <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h4>
                  <p className="text-sm text-foreground-500">中国 · 在线服务</p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-500 hover:text-background-50 text-foreground-500 transition-colors duration-200" rel="nofollow" aria-label="微博">
                <i className="ri-weibo-line text-sm" />
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-500 hover:text-background-50 text-foreground-500 transition-colors duration-200" rel="nofollow" aria-label="微信">
                <i className="ri-wechat-line text-sm" />
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-500 hover:text-background-50 text-foreground-500 transition-colors duration-200" rel="nofollow" aria-label="Telegram">
                <i className="ri-telegram-line text-sm" />
              </a>
            </div>
          </div>

          <div>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-lg p-6 md:p-8"
            >
              <h3 className="text-lg font-bold font-serif text-foreground-900 mb-6">
                发送消息
              </h3>

              {formState === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                    <i className="ri-check-line text-2xl" />
                  </div>
                  <h4 className="text-lg font-semibold text-foreground-900 mb-2">消息已发送</h4>
                  <p className="text-sm text-foreground-500">感谢您的来信，我们会尽快回复您。</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-red-400">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-red-400">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      placeholder="请输入消息主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                  </div>

                  <input
                    name="phone_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  {formState === 'error' && formError && (
                    <div className="text-sm text-red-500 bg-red-50 px-4 py-2.5 rounded-md">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'loading'}
                    className="bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 px-6 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap transition-colors duration-200 cursor-pointer"
                  >
                    {formState === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}